import { Injectable } from '@angular/core';
import { ReportHtml } from 'tms-lib';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TmsWebBaseEditService } from 'tms-web-shared';
import {
  Piece,
  PieceAllocation,
  PieceIntoStock,
  PieceIntoStockResult,
  PieceStatusItem,
  PieceStock,
  PieceTraceability,
} from '../../../models';

@Injectable({
  providedIn: 'root',
})
// Edit only - pieces are created and cancelled by the line's quantity, never directly,
// so the piece number always follows the {order}{sequence} rule.
export class PieceService extends TmsWebBaseEditService<Piece, Piece> {
  constructor() {
    super('Piece', $localize`Piece`);
  }

  getForProductionOrder(productionOrderHeaderId: string): Observable<Piece[]> {
    return this.httpService.get<Piece[]>(`${this.apiUrl}/ProductionOrder/${productionOrderHeaderId}`);
  }

  getForProductionOrderLine(productionOrderLineId: string): Observable<Piece[]> {
    return this.httpService.get<Piece[]>(`${this.apiUrl}/ProductionOrderLine/${productionOrderLineId}`);
  }

  getStatusHistory(pieceId: string): Observable<PieceStatusItem[]> {
    return this.httpService.get<PieceStatusItem[]>(`${this.apiUrl}/StatusHistory/${pieceId}`);
  }

  // The stock this piece became - more than one row only after a split.
  getStock(pieceId: string): Observable<PieceStock[]> {
    return this.httpService.get<PieceStock[]>(`${this.apiUrl}/Stock/${pieceId}`);
  }

  intoStock(model: PieceIntoStock): Observable<PieceIntoStockResult> {
    return this.httpService.post<PieceIntoStockResult>(`${this.apiUrl}/IntoStock`, model);
  }

  // Where the piece ended up - allocation, despatch and invoice.
  getTraceability(pieceId: string): Observable<PieceTraceability[]> {
    return this.httpService.get<PieceTraceability[]>(`${this.apiUrl}/Traceability/${pieceId}`);
  }

  // Splits the piece into children, one per quantity. Optional and recursive - a child
  // can itself be split. Refused once the piece is in stock: splitting STOCK is a
  // different mechanism.
  split(pieceId: string, quantities: number[]): Observable<string[]> {
    return this.httpService.post<string[]>(`${this.apiUrl}/Split/${pieceId}`, quantities);
  }

  getChildren(pieceId: string): Observable<Piece[]> {
    return this.httpService.get<Piece[]>(`${this.apiUrl}/Children/${pieceId}`);
  }

  // Dyeing reservations held against this piece.
  getAllocations(pieceId: string): Observable<PieceAllocation[]> {
    return this.httpService.get<PieceAllocation[]>(`${this.apiUrl}/Allocations/${pieceId}`);
  }

  saveAllocation(model: PieceAllocation): Observable<string> {
    return this.httpService.post<string>(`${this.apiUrl}/SaveAllocation`, model);
  }

  deleteAllocation(allocationId: string): Observable<any> {
    return this.httpService.delete(`${this.apiUrl}/Allocation/${allocationId}`);
  }

  // The cutting instruction HTML. Requested unwrapped - the in-app preview is already
  // styled, and the wrapper is only for the standalone print.
  getCutInstruction(pieceId: string): Observable<string> {
    return this.httpService
      .get<ReportHtml>(`${this.apiUrl}/CutInstruction/${pieceId}`)
      .pipe(map((model: ReportHtml) => model.html));
  }
}
